import React, { useEffect, useRef, useState } from "react";
import {
  startGroove,
  audioNow,
  audioSupported,
  notesFor,
  COMP_PATTERNS,
} from "../../lib/keyboardPlan";
import { JAM_KEYS, jamProgsFor, degStr } from "../../lib/numberSenseData";
import { Icon, Keys, Piano, MidiBadge } from "../numberSenseUi";
import useMidi from "./useMidi";

/* ============================================================
   Number Sense — the jam room (rhythm section).
   An open, ungraded space: pick a key + a loop + a feel, hit play,
   and a synth groove (drums + bass on the changes) loops while you
   comp along on a MIDI keyboard or the on-screen piano. "comp for me"
   lets the app play the chords in a chosen rhythm so you can copy it.

   Props:
     settings : { muted, ... }   only used to nudge "unmute to hear it"
     onBack   : () => void
   ============================================================ */
const CAN_PLAY = audioSupported();
const PATTERN_KEYS = Object.keys(COMP_PATTERNS);

const Pill = ({ on, onClick, children, mono }) => (
  <button className={`jam-pill ${on ? "on" : ""} ${mono ? "mono" : ""}`} onClick={onClick}>
    {children}
  </button>
);

const JamScreen = ({ settings = {}, onBack }) => {
  const [key, setKey] = useState("C");
  const [progIdx, setProgIdx] = useState(0);
  const [bpm, setBpm] = useState(100);
  const [pattern, setPattern] = useState("pads");
  const [comp, setComp] = useState(false);
  const [rootless, setRootless] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [curBar, setCurBar] = useState(0);
  const grooveRef = useRef(null);

  const progs = jamProgsFor(key);
  const safeIdx = Math.min(progIdx, progs.length - 1);
  const degrees = progs[safeIdx] || [];
  const curDeg = degrees[curBar];
  const curNotes = (curDeg && notesFor(key, curDeg)) || [];

  // MIDI just needs to sound notes while jamming (the hook plays note-ons), so
  // the handler is a no-op; we only read status for the badge.
  const midi = useMidi(() => {});

  // Groove lifecycle: (re)start whenever a musical choice changes while playing.
  useEffect(() => {
    if (!playing) return undefined;
    const g = startGroove({ key, degrees, bpm, pattern, comp, rootless });
    grooveRef.current = g;
    return () => {
      g.stop();
      grooveRef.current = null;
    };
    // degrees is derived from key + progIdx (listed); pattern/comp/rootless/bpm too
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, key, progIdx, bpm, pattern, comp, rootless]);

  // Highlight the current bar against the audio clock.
  useEffect(() => {
    if (!playing) {
      setCurBar(0);
      return undefined;
    }
    const id = setInterval(() => {
      const g = grooveRef.current;
      if (!g || !g.barDur || !g.bars) return;
      const el = audioNow() - g.startTime;
      if (el < 0) return;
      setCurBar(Math.floor(el / g.barDur) % g.bars);
    }, 80);
    return () => clearInterval(id);
  }, [playing]);

  const changeKey = (k) => {
    setKey(k);
    setProgIdx(0);
  };

  if (!CAN_PLAY) {
    return (
      <div className="ns-root">
        <div className="ns-app">
          <div className="subbar">
            <button className="iconbtn" onClick={onBack} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">jam room</div>
          </div>
          <div className="scroll">
            <div className="ns-stats-empty">
              <Keys />
              <p>this browser can't synthesize audio, so the jam room is quiet here.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">jam room</div>
        </div>
        <div className="scroll">
          <p className="games-intro">
            Pick a key, a loop and a feel, then play. A groove loops while you comp
            along — on a MIDI keyboard or the keys below. Turn on “comp for me” to
            hear the pattern and copy it.
          </p>

          <div className="jam-section">key</div>
          <div className="jam-pills">
            {JAM_KEYS.map((k) => (
              <Pill key={k} on={k === key} mono onClick={() => changeKey(k)}>
                {k}
              </Pill>
            ))}
          </div>

          <div className="jam-section">loop</div>
          <div className="jam-pills">
            {progs.map((p, i) => (
              <Pill key={p.join("-")} on={i === safeIdx} mono onClick={() => setProgIdx(i)}>
                {degStr(p)}
              </Pill>
            ))}
          </div>

          <div className="jam-section">feel</div>
          <div className="jam-pills">
            {PATTERN_KEYS.map((pk) => (
              <Pill key={pk} on={pk === pattern} onClick={() => setPattern(pk)}>
                {COMP_PATTERNS[pk].label}
              </Pill>
            ))}
          </div>

          <div className="ns-set-row">
            <div className="ns-set-label">
              <div className="ns-set-h">tempo</div>
              <div className="ns-set-sub">{bpm} bpm</div>
            </div>
            <input
              type="range"
              className="ns-range"
              aria-label="tempo (bpm)"
              min={60}
              max={150}
              step={1}
              value={bpm}
              onChange={(e) => setBpm(Number(e.target.value))}
            />
          </div>

          <div className="ns-set-row">
            <div className="ns-set-label">
              <div className="ns-set-h">comp for me</div>
              <div className="ns-set-sub">the app plays the chords in this feel</div>
            </div>
            <button
              role="switch"
              aria-checked={comp}
              aria-label="comp for me"
              className={`ns-switch ${comp ? "on" : ""}`}
              onClick={() => setComp((c) => !c)}
            >
              <span className="knob" />
            </button>
          </div>

          <div className="ns-set-row">
            <div className="ns-set-label">
              <div className="ns-set-h">rootless</div>
              <div className="ns-set-sub">comp guide tones only — leave the root to the bass</div>
            </div>
            <button
              role="switch"
              aria-checked={rootless}
              aria-label="rootless"
              className={`ns-switch ${rootless ? "on" : ""}`}
              onClick={() => setRootless((r) => !r)}
            >
              <span className="knob" />
            </button>
          </div>

          {/* the loop as a chart, current bar highlighted while playing */}
          <div className="jam-chart">
            {degrees.map((d, i) => (
              <div
                className={`jam-bar ${playing && i === curBar ? "current" : ""}`}
                key={`${d}-${i}`}
              >
                <span className="mono num">{d}</span>
                <span className="chord">{(notesFor(key, d) || []).join(" ")}</span>
              </div>
            ))}
          </div>

          <div className="jam-transport">
            <button
              className={`btn ${playing ? "ghost" : ""}`}
              onClick={() => setPlaying((p) => !p)}
            >
              <Icon name="play" /> {playing ? "stop" : "play"}
            </button>
          </div>
          {settings.muted && (
            <div className="ns-set-sub" style={{ textAlign: "center" }}>
              sound is muted — unmute in settings to hear the groove
            </div>
          )}

          <Piano pressed={new Set()} onPress={() => {}} hint={curNotes} />
          <MidiBadge status={midi.status} devices={midi.devices} />

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JamScreen;
